// Responsive design hook for form components

import { useState, useEffect } from 'react'
import { ResponsiveConfig } from '../types'

export type Breakpoint = 'mobile' | 'tablet' | 'desktop'

export interface ResponsiveHookReturn {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  breakpoint: Breakpoint
  config: ResponsiveConfig
}

const DEFAULT_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024
}

const DEFAULT_RESPONSIVE_CONFIG: ResponsiveConfig = {
  mobile: {
    layout: 'stack',
    labelPosition: 'top',
    inputSize: 'lg'
  },
  tablet: {
    layout: 'inline',
    labelPosition: 'left',
    inputSize: 'md'
  },
  desktop: {
    layout: 'grid',
    labelPosition: 'left',
    inputSize: 'md'
  }
}

export function useResponsive(
  customConfig?: Partial<ResponsiveConfig>,
  customBreakpoints?: { mobile: number; tablet: number }
): ResponsiveHookReturn {
  const breakpoints = { ...DEFAULT_BREAKPOINTS, ...customBreakpoints }
  const config = { ...DEFAULT_RESPONSIVE_CONFIG, ...customConfig }
  
  const [screenSize, setScreenSize] = useState<{
    width: number
    height: number
  }>(() => {
    // Safe initialization for SSR
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }
    return { width: 1024, height: 768 } // Default for SSR
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = screenSize.width < breakpoints.mobile
  const isTablet = screenSize.width >= breakpoints.mobile && screenSize.width < breakpoints.tablet
  const isDesktop = screenSize.width >= breakpoints.tablet

  const breakpoint: Breakpoint = 
    isMobile ? 'mobile' : 
    isTablet ? 'tablet' : 
    'desktop'

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
    config
  }
}

// Hook for getting responsive classes
export function useResponsiveClasses(classMap: {
  mobile: string
  tablet: string
  desktop: string
}): string {
  const { breakpoint } = useResponsive()
  return classMap[breakpoint]
}

// Hook for responsive values
export function useResponsiveValue<T>(valueMap: {
  mobile: T
  tablet: T
  desktop: T
}): T {
  const { breakpoint } = useResponsive()
  return valueMap[breakpoint]
}